import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Warning, MagnifyingGlass, Funnel, Receipt, Airplane, EnvelopeSimple, Coins, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

const KIND_META = {
  payroll_run: { icon: Coins, color: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Payroll" },
  leave: { icon: Airplane, color: "bg-sky-50 text-sky-800 border-sky-200", label: "Leave" },
  letter: { icon: EnvelopeSimple, color: "bg-indigo-50 text-indigo-800 border-indigo-200", label: "Letter" },
  statutory: { icon: Warning, color: "bg-amber-50 text-amber-800 border-amber-200", label: "Statutory" },
  reimbursement: { icon: Receipt, color: "bg-purple-50 text-purple-800 border-purple-200", label: "Reimburse" },
  regularization: { icon: ArrowsClockwise, color: "bg-teal-50 text-teal-800 border-teal-200", label: "Regularize" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AuditLog() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(200);

  const load = () => {
    setLoading(true);
    const params = { limit };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/audit-log", { params })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, limit]);

  const filtered = useMemo(() => rows.filter((r) => {
    const matchQ = !q || (r.message || "").toLowerCase().includes(q.toLowerCase());
    const matchK = !kind || r.kind === kind;
    return matchQ && matchK;
  }), [rows, q, kind]);

  const kinds = useMemo(() => Array.from(new Set(rows.map((r) => r.kind))), [rows]);
  const kindCounts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.kind]: (acc[r.kind] || 0) + 1 }), {}), [rows]);

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Governance</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Audit log</h1>
        <p className="text-sm text-zinc-500 mt-2">Unified trail of payroll, leave, letters, reimbursements, statutory submissions and regularizations.</p>
      </div>

      {/* Kind pills with counts */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={() => setKind("")} className={`px-2.5 py-1 rounded-sm text-xs border transition-colors ${!kind ? "bg-adb-navy text-white border-adb-navy" : "bg-white border-zinc-200 hover:border-adb-navy/40"}`} data-testid="audit-kind-all">
          All <span className="ml-1 opacity-70 font-mono-plex">{rows.length}</span>
        </button>
        {kinds.map((k) => {
          const meta = KIND_META[k] || { label: k, color: "bg-zinc-50 border-zinc-200" };
          const Ico = meta.icon;
          const active = kind === k;
          return (
            <button key={k} onClick={() => setKind(k)} className={`px-2.5 py-1 rounded-sm text-xs border flex items-center gap-1.5 transition-colors ${active ? "bg-adb-navy text-white border-adb-navy" : `${meta.color}`}`} data-testid={`audit-kind-${k}`}>
              {Ico && <Ico size={12} />}
              {meta.label}
              <span className="opacity-70 font-mono-plex">{kindCounts[k] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Search + limit */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
          <Input placeholder="Search message…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm w-64" data-testid="audit-search" />
        </div>
        <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value))} className="border border-zinc-300 rounded-sm text-sm px-2 py-1.5 bg-white" data-testid="audit-limit">
          {[50, 100, 200, 500, 1000].map((n) => <option key={n} value={n}>{n} events</option>)}
        </select>
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="audit-refresh"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <Badge variant="outline" className="rounded-sm ml-auto">{filtered.length} shown</Badge>
      </div>

      {/* Timeline */}
      <div className="border border-zinc-200 rounded-sm bg-white divide-y divide-zinc-100" data-testid="audit-timeline">
        {loading && <div className="text-center py-8 text-sm text-zinc-500">Loading events…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 text-sm text-zinc-500">
            <Funnel size={24} className="inline text-zinc-300 mr-2" />
            No events match the current filter.
          </div>
        )}
        {!loading && filtered.map((e, i) => {
          const meta = KIND_META[e.kind] || { icon: Warning, color: "bg-zinc-50 text-zinc-800 border-zinc-200", label: e.kind };
          const Ico = meta.icon;
          return (
            <div key={i} className="flex items-start gap-3 px-4 py-2.5 hover:bg-zinc-50/50 transition-colors">
              <div className={`w-8 h-8 rounded-sm grid place-items-center shrink-0 ${meta.color}`}>
                <Ico size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-900">{e.message}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2">
                  <span>{timeAgo(e.at)}</span>
                  <span>·</span>
                  <span className="font-mono-plex">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
                  {e.actor && <><span>·</span><span className="font-mono-plex text-zinc-400">by {e.actor.substring(0, 8)}</span></>}
                </div>
              </div>
              <Badge variant="outline" className={`rounded-sm text-[10px] font-mono-plex ${meta.color}`}>{meta.label}</Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}
